import { useEffect, useMemo, useRef } from "react";
import posterDesktop from "@assets/bg-ai-desktop.jpg";
import posterMobile from "@assets/bg-ai-mobile.jpg";
import { useWindowSize } from "@hooks/useWindowSize";
import { getEnvironment } from "@util/constant";
import { getGraphicsDomain } from "@util/helpers";

/**
 * The video background component for the Asean Insights template.
 * @figma https://www.figma.com/design/9RrH1KYIUOZk6rvgzlBBKX/WEB-BAU-2026?node-id=20436-71279&m=dev
 */
const VideoBackground = (): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const videoRef = useRef<HTMLVideoElement>(null);
  const graphicsDomain = getGraphicsDomain(getEnvironment());
  const poster = isScreenLG ? posterDesktop : posterMobile;
  const key = isScreenLG
    ? "/bt_files/bg-asean-insights.mp4"
    : "/bt_files/bg-asean-insights-mobile.mp4";
  const url = useMemo(() => {
    return new URL(key, graphicsDomain);
  }, [key, graphicsDomain]);

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current.load(); // Forces the browser to re-evaluate the source
  }, [url]);

  return (
    <video
      autoPlay
      muted
      loop
      playsInline
      disablePictureInPicture
      disableRemotePlayback
      poster={poster}
      className="h-full w-full"
      data-testid="video-background-component"
      controlsList="nofullscreen nodownload"
      ref={videoRef}
      tabIndex={-1}
    >
      <source src={url.toString()} type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  );
};

export default VideoBackground;
